/**
 * useRetention - Hooks React Query do módulo de Retenção de e-mails (Plano B, Task 8)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailRetentionQuery, EmailRetentionStatus } from '@troia-v3/types';

import { retentionService } from '../services/retentionService';

/** Prefixo comum das query keys — usado pra invalidar lista + count + detail de uma vez. */
const RETENTION_KEY_ROOT = 'email-retentions' as const;

export const retentionQueryKeys = {
  root: [RETENTION_KEY_ROOT] as const,
  count: (status?: EmailRetentionStatus) => [RETENTION_KEY_ROOT, 'count', status ?? 'all'] as const,
  list: (query: EmailRetentionQuery) => [RETENTION_KEY_ROOT, 'list', query] as const,
  detail: (id: string) => [RETENTION_KEY_ROOT, 'detail', id] as const,
};

/**
 * Badge de pendentes na `TicketsFilterBar` — `refetchInterval: 60s` (CLAUDE.md
 * FE — hooks gated, badge com polling curto). `enabled` default `true`: o
 * hook só é montado quando o botão de Retenção é renderizado (não há
 * modal/drawer fechado escondendo este hook).
 */
export const useRetentionCount = (
  status: EmailRetentionStatus = 'pending',
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: retentionQueryKeys.count(status),
    queryFn: () => retentionService.getCount(status),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
};

/** Lista paginada da fila de decisão (`RetentionPage`). */
export const useRetentionList = (query: EmailRetentionQuery) => {
  return useQuery({
    queryKey: retentionQueryKeys.list(query),
    queryFn: () => retentionService.getAll(query),
    staleTime: 15 * 1000,
  });
};

/**
 * Entry completa (com `email.html`/anexos) — só busca quando `id` está
 * presente (gated ao entry selecionada, CLAUDE.md NUNCA #14).
 */
export const useRetentionEntry = (id: string | undefined) => {
  return useQuery({
    queryKey: retentionQueryKeys.detail(id ?? ''),
    queryFn: () => retentionService.getById(id as string),
    enabled: !!id,
  });
};

/**
 * Info do contato dono do e-mail — só busca quando a entry selecionada
 * carregou o `contactId` (gated, mesmo motivo do hook acima).
 */
export const useRetentionContact = (contactId: string | undefined) => {
  return useQuery({
    queryKey: ['retention-contact', contactId],
    queryFn: () => retentionService.getContact(contactId as string),
    enabled: !!contactId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Aprova/Confia a entry — invalida lista + count + detail (prefixo comum).
 * `trustContact` false = Aprovar (só desta vez); true = Confiar (pra sempre).
 * `customerId` associa o contato a um cliente.
 */
export const useApproveRetention = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; trustContact?: boolean; customerId?: string | null }) =>
      retentionService.approve(vars.id, { trustContact: vars.trustContact, customerId: vars.customerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: retentionQueryKeys.root });
    },
  });
};

/** Descarta a entry (sem criar ticket, sem tocar o contato). */
export const useDiscardRetention = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => retentionService.discard(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: retentionQueryKeys.root });
    },
  });
};

/** Bloqueia a entry (+ pendentes do mesmo contato em lote no backend). */
export const useBlockRetention = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => retentionService.block(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: retentionQueryKeys.root });
    },
  });
};
